import * as React from 'react';
import { useRouter } from 'next/router';
import { baseUrl } from 'lib/base-url';
import { StructuredData } from 'components/structured-data';
import { BlockProps } from '../index.types';
import PageSection from 'components/page/page-section';
import { Header3 } from 'components/typography';
import { Paragraph } from 'components/Blocks/Paragraph/Paragraph';
import { TextTagName } from 'components/typography/text';
import Icon from 'components/icon/icon';
import Link from 'next/link';

type FaqImgAnswerType = {
    key: null;
    type: 'img';
    props: {
        alt: string;
        className: string;
        src: string;
    };
    ref: null;
    _owner: null;
};

type FaqQuestionType = {
    id: string;
    question?: string[];
    jsonQuestion: string;
    jsonAnswer?: string;
    answer: string[] | FaqImgAnswerType[];
};

type parsedFaqQuestionType = FaqQuestionType & {
    url: string;
};

function FAQListItem({ id, jsonQuestion, jsonAnswer }: FaqQuestionType) {
    const [open, setOpen] = React.useState(false);

    return (
        <React.Fragment key={`schema-faq-question-${id}`}>
            <li className="schema-faq__question" id={id} key={id}>
                <div className="schema-faq__question-text">
                    <Link
                        className="schema-faq-question__action"
                        href={`#${id}`}
                        onClick={(event) => {
                            event.preventDefault();
                            setOpen((value) => !value);
                        }}
                    >
                        <Header3 html={jsonQuestion} tagname={TextTagName.h2} />

                        <div
                            className={`schema-faq-question__action-icon ${
                                open ? 'is-active' : ''
                            }`}
                        >
                            <Icon size="stretch" name={Icon.Name.BumbleOpen} />
                        </div>
                    </Link>
                </div>
                <div className={`schema-faq__question-answer ${open ? 'is-open' : ''}`}>
                    {jsonAnswer ? (
                        <div className="schema-faq__question-answer-faq">
                            <Paragraph
                                block={{
                                    innerHTML: jsonAnswer,
                                }}
                            />
                        </div>
                    ) : null}
                </div>
            </li>
        </React.Fragment>
    );
}

function setParsedQuestion({ id, jsonQuestion, jsonAnswer, answer }: parsedFaqQuestionType) {
    return (
        <React.Fragment key={`schema-faq-question-${id}`}>
            <FAQListItem
                id={id}
                jsonQuestion={jsonQuestion}
                answer={answer}
                jsonAnswer={jsonAnswer}
            />
        </React.Fragment>
    );
}

export function FAQList({ questions }: BlockProps) {
    const router = useRouter();
    const url = `${baseUrl}${router.basePath}${router.asPath}`;
    const parsedQuestions = (questions && JSON.parse(questions)) || [];

    const questionsList = parsedQuestions.map((parsedQuestion: FaqQuestionType) =>
        setParsedQuestion({ url, ...parsedQuestion })
    );

    return (
        <PageSection>
            <ul className="schema-faq">{questionsList}</ul>
            <StructuredData
                id="faq-page"
                type={StructuredData.type.FAQPage}
                data={{
                    items: parsedQuestions.map(({ jsonQuestion, answer }: FaqQuestionType) => {
                        return {
                            name: jsonQuestion,
                            // @TODO update to include any string or image
                            item: typeof answer[0] === 'string' ? answer[0] : '',
                        };
                    }),
                }}
            />
        </PageSection>
    );
}
